<template>
    <div v-if="configIsLoaded">
        <form
            class="form"
            @submit.prevent="handleSubmit"
            novalidate
        >
            <fieldset class="form__fieldset">
                <legend class="form__legend">Kaufanfrage</legend>
                <div class="form__fieldset-fields">
                    <BaseInput
                        v-model.trim="formData.firstname"
                        label="Vorname"
                        type="text"
                        required
                        :error="formError.firstname"
                    />
                    <BaseInput
                        v-model.trim="formData.lastname"
                        label="Nachname"
                        type="text"
                        required
                        :error="formError.lastname"
                    />
                    <BaseInput
                        v-model.trim="formData.phone"
                        label="Telefon"
                        placeholder="+49123456789"
                        type="tel"
                        required
                        :error="formError.phone"
                    />
                    <BaseInput
                        v-model.trim="formData.email"
                        label="E-Mail Adresse"
                        placeholder="max@mustermann.de"
                        type="email"
                        required
                        :error="formError.email"
                    />
                </div>
                <div class="form__fieldset-fields form__fieldset-fields--single">
                    <BaseTextarea
                        v-model.trim="formData.message"
                        label="Nachricht"
                        required
                        :error="formError.message"
                    />
                </div>
            </fieldset>

            <fieldset class="form__fieldset">
                <legend class="form__legend">Datenschutz</legend>
                <div class="form__fieldset-fields form__fieldset-fields--single">
                    <BaseCheckbox
                        label="Ich habe die {Datenschutzerklärung} gelesen und verstanden."
                        :label-link="config.termsUrl"
                        v-model="formData.terms"
                        :error="formError.terms"
                        required
                    />
                </div>
            </fieldset>

            <div class="form__button-group">
                <button
                    class="button-primary form__button"
                    type="submit"
                    :disabled="isSubmitting"
                >
                    <span class="button-primary__label">
                        {{ isSubmitting ? 'Wird gesendet...' : 'Absenden' }}
                    </span>
                    <span class="button-primary__icon">
                        <BaseIcon name="ri-arrow-right-up-line" />
                    </span>
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import BaseIcon from "../component/BaseIcon.vue";
import { onMounted, reactive, ref } from 'vue';
import FormService from "../service/FormService";
import { OverlayService } from '../service/OverlayService';
import BaseInput from "../component/BaseInput.vue";
import BaseCheckbox from "../component/BaseCheckbox.vue";
import BaseTextarea from "../component/BaseTextarea.vue";

const props = defineProps({
    pageUuid: String
});

const isSubmitting = ref(false);
const statusMessage = ref('');
const configIsLoaded = ref(false);
const token = ref();

const config = ref({
    termsUrl: null,
});

const formData = reactive({
    terms: false,
    firstname: '',
    lastname: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
    url: '',
});

const formError = reactive({
    terms: null,
    firstname: null,
    lastname: null,
    phone: null,
    email: null,
    message: null,
});

const loadFormConfig = async () => {
    try {
        const response = await FormService.config({
            formType: 'sale',
            pageUuid: props.pageUuid,
        });

        if (response.status === 200) {
            formData.subject = response.data.config.title;
            formData.url = response.data.config.url;
            token.value = response.data.config.token;
            config.value = response.data.config;
            configIsLoaded.value = true;
        } else {
            OverlayService.open({
                type: 'error',
                title: 'Konfigurationsfehler',
                message: 'Hoppla! Etwas ist schief gelaufen beim Laden der Formular-Konfiguration.'
            });
        }
    } catch (error) {
        OverlayService.open({
            type: 'error',
            message: 'Fehler: Verbindung zum Server fehlgeschlagen.'
        });
    }
};

const mapApiErrorsToForm = (apiErrors) => {
    if (apiErrors.mainMessage) {
        OverlayService.open({
            type: 'error',
            message: apiErrors.mainMessage
        });
    }

    Object.keys(formError).forEach(fieldName => {
        if (apiErrors[fieldName]) {
            const errorValue = apiErrors[fieldName];
            formError[fieldName] = Array.isArray(errorValue) ? errorValue[0] : errorValue;
        }
    });
};

const handleSubmit = async () => {
    isSubmitting.value = true;
    statusMessage.value = '';

    Object.keys(formError).forEach(key => formError[key] = null);

    try {
        const payload = { ...formData };
        const response = await FormService.sale(payload, token.value);

        if (response.status === 200) {
            if (!response.data.success) {
                mapApiErrorsToForm(response.data.errors);
            } else {
                OverlayService.open({
                    type: 'success',
                    title: 'Erfolgreich gesendet',
                    message: response.data.success || 'Vielen Dank für Ihre Nachricht!'
                });
                // Formular zurücksetzen bei Erfolg
                Object.keys(formData).forEach(key => {
                    if (typeof formData[key] === 'boolean') {
                        formData[key] = false;
                    } else {
                        formData[key] = '';
                    }
                });
            }
        } else {
            OverlayService.open({
                type: 'error',
                message: 'Hoppla! Etwas ist schief gelaufen.'
            });
        }
    } catch (error) {
        console.error(error);
        OverlayService.open({
            type: 'error',
            message: 'Fehler: Verbindung zum Server fehlgeschlagen.'
        });
    } finally {
        isSubmitting.value = false;
    }
};

onMounted(() => {
    loadFormConfig();
});

</script>
